<template>
  <div
    class="sider-map-section"
    ref="sider-map-section"
    :class="{ 'sider-map-section__hidden': !isSiderOpened }"
    style="overflow-y: auto"
  >
    <data-input v-model="searchValue" color="grey darken-1" v-bind="searchTypeInput" class="solo-input px-4" />
    <v-list style="text-align: left !important" dense :class="{ 'pb-4': hasStickyFooter }">
      <template v-for="(element, idx) in searchedItems">
        <v-tooltip bottom color="font" :disabled="!isModifySnappingActive" :key="idx">
          <template #activator="{ on }">
            <span v-on="on">
              <v-list-item
                class="sider-list-item"
                :class="{
                  'sider-map-item-active': isMapView && element.id === projectId,
                  'sider-list-item-disabled': isModifySnappingActive,
                }"
                @click="openMap(element.id)"
                :disabled="isModifySnappingActive || (isMapView && !isProjectLayersFetched)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <v-row no-gutters align="center" class="flex-nowrap">
                      <v-col cols="auto">
                        <v-icon class="pb-1 pr-2" size="18" :style="`color: ${element.iconColor} !important`">
                          {{ element.prependIcon }}
                        </v-icon>
                      </v-col>
                      <v-col
                        cols="auto"
                        class="flex-shrink-1"
                        style="overflow: hidden; white-space: nowrap; text-overflow: ellipsis"
                      >
                        {{ element.name }}
                      </v-col>
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </span>
          </template>
          {{ changeProjectTooltip }}
        </v-tooltip>
      </template>
    </v-list>
    <v-row
      no-gutters
      :class="{ 'sider-map-section-sticky': hasStickyFooter }"
      class="pb-3"
      align="center"
      justify="center"
    >
      <v-col cols="12" class="px-5">
        <base-button @click="openMaps" translation-path="maps.manageMaps" style="width: 100%" />
      </v-col>
    </v-row>
  </div>
</template>
<script>
let resizeObserver;
import { get, sync } from 'vuex-pathify';
import { debounce } from 'lodash';

import DataInput from '@/components/DataInput';

export default {
  name: 'SiderMapSection',
  components: {
    DataInput,
  },
  props: {
    isSiderOpened: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    currentUserId: get('users/currentUser@id'),
    project: sync('layers/project'),
    projectId: get('layers/project.id'),
    projects: get('layers/projects'),

    isMapViewChanged: sync('map/isMapViewChanged'),
    isProjectLayersFetched: sync('sidebar/isProjectLayersFetched'),
    isModifySnappingActive: get('edit/isModifySnappingActive'),
    changeProjectTooltip() {
      return this.isModifySnappingActive
        ? this.$i18n.t('button.toChangeProject', { reason: this.$i18n.t('button.turnOffEditing') })
        : '';
    },
    isMapView() {
      return this.$route.path?.split('/')[1] === 'map';
    },

    projectsDict() {
      return {
        default: {
          icon: 'mdi-home',
          iconColor: '#00000096',
        },
        predefined: {
          icon: 'mdi-star',
          iconColor: '#EFB100',
        },
        owner: {
          icon: 'mdi-account',
          iconColor: '#323232',
        },
        shared: {
          icon: 'mdi-account-group',
          iconColor: '#00000096',
        },
      };
    },
    projectsArray() {
      const reducedProjects = [...this.$_objectToArray(this.projects)].reduce((acc, item) => {
        const isOwner = this.currentUserId === item.owner;
        const attr = item['role'] ? item['role'] : isOwner ? 'owner' : 'shared';
        if (!acc[attr]) acc[attr] = [];
        acc[attr].push(item);
        return acc;
      }, {});
      const order = Object.keys(this.projectsDict);
      return Object.entries(reducedProjects)
        .sort(([aKey], [bKey]) => order.indexOf(aKey) - order.indexOf(bKey))
        .flatMap(([key, items]) =>
          items
            .map(item => ({
              id: item.id,
              name: item.name,
              role: key,
              prependIcon: this.projectsDict[key].icon,
              iconColor: this.projectsDict[key].iconColor,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
    },
    searchTypeInput() {
      return {
        dataType: { name: 'text' },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
  },
  data: () => ({
    hasStickyFooter: false,
    searchValue: '',
    searchedItems: [],
  }),
  watch: {
    searchValue: {
      handler(nV) {
        this.search(nV);
      },
      immediate: true,
    },
  },
  methods: {
    openMap(projectId) {
      if (this.isMapView) {
        if (projectId === this.projectId) return;
        const project = JSON.parse(JSON.stringify(this.projects[projectId]));
        this.$root.$emit('projectChanged', { e: project, withRouterParse: true });
      } else {
        this.$router.push({ name: 'map', query: { project: projectId } });
      }
    },
    openMaps() {
      this.$router.push({ name: 'maps' });
    },
    resizeObserverCallback() {
      if (!this.$refs['sider-map-section']) return;
      const { scrollHeight, clientHeight } = this.$refs['sider-map-section'];
      this.hasStickyFooter = scrollHeight >= clientHeight;
    },
    search(value = this.searchValue) {
      if (!value) {
        this.searchedItems = [...this.projectsArray];
      } else {
        this.searchedItems = this.projectsArray.filter(project =>
          project.name?.toLowerCase().includes(value.toLowerCase())
        );
      }
    },
  },
  created() {
    this.search = debounce(this.search, 500);
  },
  mounted() {
    resizeObserver = new ResizeObserver(this.resizeObserverCallback);
    resizeObserver.observe(this.$refs['sider-map-section']);
  },
};
</script>
<style lang="scss" scoped>
.sider-map-section {
  height: 100%;
  transition: opacity 0.2s ease allow-discrete;
}
.sider-map-section-sticky {
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: white;
}
.sider-map-section__hidden {
  opacity: 0 !important;
  pointer-events: none !important;
}
.sider-map-item-active {
  background-color: map-get($colors, ghost);
}
.sider-list-item-disabled {
  color: map-get($colors, disabled) !important;
}
</style>
